import React, { useEffect, useState } from 'react';

import { Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import { ValidatorForm } from 'react-material-ui-form-validator';

import { ButtonComponent } from 'components/ButtonComponent/ButtonComponent';
import { TextValidatorComponent, AutocompleteValidatorComponent } from 'components/FormComponents';
import { VALIDATORS_MAP } from 'components/FormComponents/const';
import { useAuthState } from 'contexts/AuthContext';
import { adminsModel } from 'models';
import { ROLES } from 'routes/constants';
import { useSubmittingState } from 'hooks';

import { Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, Box } from '../style';

const TITLE = {
  [ROLES.clientModerator]: 'Add New Space Moderator',
  [ROLES.clientAdmin]: 'Add New Space Admin',
  [ROLES.systemAdmin]: 'Add New System Admin',
};

export function AddAdminDialog({
  open,
  handleClose,
  handleServerError,
  clients,
  afterActions,
  resetServerErrors,
  creatingAdminRole,
}) {
  const { currentUser } = useAuthState();
  const defaultAdmin = {
    password: '',
    passwordConfirmation: '',
    email: '',
    clientId: currentUser.attributes.role === ROLES.systemAdmin ? '' : currentUser.attributes.clientId,
    role: creatingAdminRole,
    otpNeedEnable: true,
  };
  const [admin, setAdmin] = useState(defaultAdmin);
  const { isSubmitting, withHandlingSubmittingState } = useSubmittingState();

  useEffect(() => resetForm, [open]);

  const resetForm = () => {
    setAdmin(defaultAdmin);
    resetServerErrors();
  };

  const handleSubmit = () =>
    adminsModel
      .addAdmin(admin)
      .then(() => setAdmin(defaultAdmin))
      .then(afterActions)
      .then(handleClose)
      .catch(handleServerError);

  const handleChange = item => setAdmin(prevState => ({ ...prevState, ...item }));

  const onCheckedCheckboxChange = ({ target: { checked } }) => handleChange({ otpNeedEnable: checked });

  return (
    <Dialog fullWidth open={open} onClose={handleClose}>
      <ValidatorForm onSubmit={() => withHandlingSubmittingState(handleSubmit)()} autoComplete="off">
        <DialogTitle>{TITLE[creatingAdminRole]}</DialogTitle>
        <DialogContent>
          {currentUser.attributes.role === ROLES.systemAdmin && clients.length ? (
            <div>
              <AutocompleteValidatorComponent
                value={admin.clientId}
                validators={[VALIDATORS_MAP.required]}
                fieldName="clientId"
                handleChange={handleChange}
                selectList={clients}
                label="Organization"
                itemsNamePath="name"
              />
            </div>
          ) : null}
          <TextValidatorComponent
            validators={[VALIDATORS_MAP.required, VALIDATORS_MAP.isEmail]}
            label="Email"
            value={admin.email}
            fieldName="email"
            handleChange={handleChange}
            type="email"
            autoComplete="do-not-show-ac"
          />
          <Box>
            <Typography variant="caption">
              <Checkbox
                color="primary"
                checked={admin.otpNeedEnable}
                onChange={onCheckedCheckboxChange}
                disabled={creatingAdminRole === ROLES.systemAdmin}
              />
              <span>Two-Factor authentication</span>
            </Typography>
          </Box>
          <TextValidatorComponent
            validators={[VALIDATORS_MAP.required]}
            label="Password"
            value={admin.password}
            fieldName="password"
            handleChange={handleChange}
            type="password"
            autoComplete="do-not-show-ac"
          />
          <TextValidatorComponent
            validators={[VALIDATORS_MAP.required]}
            label="Password confirmation"
            value={admin.passwordConfirmation}
            fieldName="passwordConfirmation"
            handleChange={handleChange}
            type="password"
          />
        </DialogContent>
        <DialogActions>
          <ButtonComponent text="Cancel" onClick={handleClose} />
          <ButtonComponent text="Save" type="submit" color="primary" loaderSize="1.35em" loading={isSubmitting} />
        </DialogActions>
      </ValidatorForm>
    </Dialog>
  );
}

AddAdminDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleServerError: PropTypes.func.isRequired,
  afterActions: PropTypes.func.isRequired,
  clients: PropTypes.arrayOf(PropTypes.shape({})),
  resetServerErrors: PropTypes.func.isRequired,
  creatingAdminRole: PropTypes.string.isRequired,
};

AddAdminDialog.defaultProps = {
  clients: [],
};
